import React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import CategoryRow from './category_row';
import SubcategoryRow from './subcategory_row';
import { useDispatch, useSelector } from 'react-redux';
import { reorderSubcategories, reorderCategories } from '../../actions';

const CategoryTable = ({
  grouped,
  expanded,
  toggleExpanded,
  handleEditClick,
  handleDelete,
  toggleCategory
}) => {
  const dispatch = useDispatch();
  const buttons = useSelector(state => state.buttons);
  const isClubPremium = useSelector(state => state.club_premium);

  const categories = grouped;
  const draggableStart = 2;
  const draggableEnd = categories.length - 3;

  const onDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;

    if (type === 'CATEGORY') {
      if (
        source.index === destination.index ||
        source.index < draggableStart || source.index > draggableEnd ||
        destination.index < draggableStart || destination.index > draggableEnd
      ) return;

      const reordered = Array.from(categories);
      const [moved] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, moved);

      const updated = reordered.map((cat, index) => ({
        ...cat,
        order_number: index + 1,
      }));

      dispatch(reorderCategories(updated));
    }

    if (type === 'SUBCATEGORY') {
      const categoryId = source.droppableId;
      const currentSubcategories = buttons
        .filter(btn => btn.parent_id === categoryId)
        .sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0));

      const newSubcategories = Array.from(currentSubcategories);
      const [moved] = newSubcategories.splice(source.index, 1);
      newSubcategories.splice(destination.index, 0, moved);

      const reordered = newSubcategories.map((sub, idx) => ({
        ...sub,
        order_number: idx + 1,
      }));

      dispatch(reorderSubcategories(categoryId, reordered));
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="categories" type="CATEGORY">
        {(categoriesProvided) => (
          <table
            className="table categories-table"
            ref={categoriesProvided.innerRef}
            {...categoriesProvided.droppableProps}
          >
            <tbody>
              {categories.map((category, index) => {
                const isDraggable = index >= draggableStart && index <= draggableEnd;

                const categoryRow =
                  <Draggable
                    key={`category-${category.id}`}
                    draggableId={`category-${category.id}`}
                    index={index}
                    isDragDisabled={!isDraggable}
                  >
                    {(prov) => (
                      <CategoryRow
                        category={category}
                        isExpanded={expanded[category.id]}
                        onToggle={() => toggleExpanded(category.id)}
                        onEdit={() => handleEditClick(category)}
                        onDelete={() => handleDelete(category.id)}
                        onCheckboxClick={(e) => {
                          e.stopPropagation();
                          if (!category.default) dispatch(toggleCategory(category.id));
                        }}
                        draggableProps={prov.draggableProps}
                        dragHandleProps={prov.dragHandleProps}
                        innerRef={prov.innerRef}
                        isDraggable={isDraggable}
                        isClubPremium={isClubPremium}
                      />
                    )}
                  </Draggable>

                const subcategoryRow = expanded[category.id] && (
                  <tr key={`sub-${category.id}`}>
                    <td className="expanded-table-td" colSpan={5}>
                      <Droppable droppableId={category.id.toString()} type="SUBCATEGORY">
                        {(provided) => (
                          <table
                            className="table table-striped subcategory-table"
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            <tbody>
                              {category.subcategories.map((sub, idx) => (
                                <Draggable
                                  key={sub.id.toString()}
                                  draggableId={sub.id.toString()}
                                  index={idx}
                                >
                                  {(prov) => (
                                    <SubcategoryRow
                                      subcategory={sub}
                                      provided={prov}
                                      onEdit={() => handleEditClick(sub)}
                                      onDelete={() => handleDelete(sub.id)}
                                      onCheckboxClick={() => {
                                        if (!sub.default) dispatch(toggleCategory(sub.id));
                                      }}
                                      isClubPremium={isClubPremium}
                                    />
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </tbody>
                          </table>
                        )}
                      </Droppable>
                    </td>
                  </tr>
                );

                return (
                  <React.Fragment key={category.id}>
                    {categoryRow}
                    {subcategoryRow}
                  </React.Fragment>
                );
              })}
              {categoriesProvided.placeholder}
            </tbody>
          </table>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default CategoryTable;
